import { supabase } from "@shared/integrations/supabase/client";
import type { Database as SupabaseDatabase } from "@/integrations/supabase/types";
import type { Project } from "@/components/ProjectView";
import { normalizeCode } from "@/lib/codes";
import type { Code } from "@/lib/codes";
import { isUserIdAdmin } from "@/lib/userRoles";

type CodeRow = SupabaseDatabase["public"]["Tables"]["codes"]["Row"];
type DatasetRow = SupabaseDatabase["public"]["Tables"]["test_data_sets"]["Row"];
type TestRunRow = Pick<
	SupabaseDatabase["public"]["Tables"]["test_runs"]["Row"],
	"test_data_set_id" | "project_id"
>;

type Dataset = DatasetRow & {
	projects?: { id: string; title: string }[];
	derivatives?: { id: string; name: string }[];
};

const normalizeProjectsWithOwner = (
	rows: SupabaseDatabase["public"]["Tables"]["projects"]["Row"][],
	profiles: Record<string, string>,
): Project[] =>
	rows.map((project) => ({
		id: project.id,
		title: project.title,
		created_at: project.created_at,
		updated_at: project.updated_at,
		user_id: project.user_id,
		is_public: project.is_public,
		agent_id: project.agent_id,
		code_id: project.code_id,
		behavior_dataset_id: project.behavior_dataset_id,
		behavior_dataset_selected_at: project.behavior_dataset_selected_at,
		rules_dataset_id: project.rules_dataset_id,
		rules_dataset_selected_at: project.rules_dataset_selected_at,
		owner_email: profiles[project.user_id] ?? "Unknown",
	}));

const normalizeCodesWithOwner = (
	rows: CodeRow[],
	profiles: Record<string, string>,
): Code[] =>
	rows.map((code) => normalizeCode(code, profiles[code.user_id] ?? "Unknown"));

const attachProjectsToDatasets = (
	datasets: DatasetRow[],
	runs: TestRunRow[] | null,
	projectsById: Map<string, { id: string; title: string | null }>,
): Dataset[] => {
	if (!runs || runs.length === 0) {
		return datasets.map((dataset) => ({ ...dataset, projects: [] }));
	}

	const runsByDataset = new Map<string, { id: string; title: string }[]>();
	runs.forEach((run) => {
		if (!run.test_data_set_id || !run.project_id) return;
		const project = projectsById.get(run.project_id);
		if (!project) return;
		const existing = runsByDataset.get(run.test_data_set_id) ?? [];
		if (!existing.some((entry) => entry.id === project.id)) {
			runsByDataset.set(run.test_data_set_id, [
				...existing,
				{ id: project.id, title: project.title ?? "Untitled Project" },
			]);
		}
	});

	return datasets.map((dataset) => ({
		...dataset,
		projects: runsByDataset.get(dataset.id) ?? [],
	}));
};

export const allDatasetsLoader = async () => {
	const { data: authData, error: authError } = await supabase.auth.getUser();
	if (authError || !authData?.user) {
		throw new Response("Unable to load datasets without a user", {
			status: 401,
		});
	}

	const userId = authData.user.id;

	const userIsAdmin = await isUserIdAdmin(userId);

	const [datasetsRes, projectsRes, codesRes] = await Promise.all([
		userIsAdmin
			? supabase
					.from("test_data_sets")
					.select("*")
					.order("updated_at", { ascending: false })
			: supabase
					.from("test_data_sets")
					.select("*")
					.or(`user_id.eq.${userId},is_public.eq.true`)
					.order("updated_at", { ascending: false }),
		supabase
			.from("projects")
			.select("*")
			.order("updated_at", { ascending: false }),
		supabase.from("codes").select("*").order("updated_at", { ascending: false }),
	]);

	if (datasetsRes.error) throw datasetsRes.error;
	if (projectsRes.error) throw projectsRes.error;
	if (codesRes.error) throw codesRes.error;

	const datasetRows = datasetsRes.data ?? [];
	const projectRows = projectsRes.data ?? [];
	const codeRows = codesRes.data ?? [];

	let datasetsWithProjects: Dataset[] = datasetRows.map((dataset) => ({
		...dataset,
		projects: [],
	}));

	const projectsById = new Map(
		projectRows.map((project) => [project.id, project]),
	);

	if (datasetRows.length > 0) {
		const datasetIds = datasetRows.map((dataset) => dataset.id);
		const { data: runsData, error: runsError } = await supabase
			.from("test_runs")
			.select("test_data_set_id, project_id")
			.in("test_data_set_id", datasetIds);

		if (runsError) {
			throw runsError;
		}

		datasetsWithProjects = attachProjectsToDatasets(
			datasetRows,
			runsData as TestRunRow[] | null,
			projectsById,
		);
	}

	// Attach adversarial datasets to primary datasets
	const derivativesByParent = new Map<string, { id: string; name: string }[]>();
	for (const dataset of datasetRows) {
		if (dataset.type === "adversarial" && dataset.parent_id) {
			const existing = derivativesByParent.get(dataset.parent_id) ?? [];
			derivativesByParent.set(dataset.parent_id, [
				...existing,
				{ id: dataset.id, name: dataset.name },
			]);
		}
	}

	datasetsWithProjects = datasetsWithProjects.map((dataset) => {
		if (dataset.type === "primary") {
			return {
				...dataset,
				derivatives: derivativesByParent.get(dataset.id) ?? [],
			};
		}
		return dataset;
	});

	const profileUserIds = new Set<string>();
	for (const row of [...projectRows, ...codeRows]) {
		profileUserIds.add(row.user_id);
	}

	let profileMap: Record<string, string> = {};

	if (profileUserIds.size > 0) {
		const { data: profilesData, error: profilesError } = await supabase
			.from("profiles")
			.select("user_id, display_name, email")
			.in("user_id", [...profileUserIds]);

		if (profilesError) throw profilesError;

		profileMap = Object.fromEntries(
			(profilesData ?? []).map((profile) => [
				profile.user_id,
				profile.display_name ?? profile.email ?? "Unknown",
			]),
		);
	}

	return {
		datasets: datasetsWithProjects,
		projects: normalizeProjectsWithOwner(projectRows, profileMap),
		codes: normalizeCodesWithOwner(codeRows, profileMap),
	};
};
